// src/modules/app-views/serviceOrders/pages/Document/index.jsx
// *******************************************************************************
// SERVICE ORDERS DOCUMENT PAGE
// *******************************************************************************
// Main page for creating and editing service orders (budgets and orders).
// Contains tabs for Record and Items.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs } from 'antd';
import { FormOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useFormInstance from 'hooks/useFormInstance';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import ActionsFloatButton from 'components/JarvislyModule/ActionsFloatButton';
import serviceOrdersDocumentFloatButton from './actions/floatButton';
import ServiceOrdersRecordTab from './tabs/RecordTab';
import ServiceOrdersItemsTab from './tabs/ItemsTab';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const TAB_LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 6 };
const STYLES = {
  cardHeader: { position: 'relative' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServiceOrdersDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, refresh, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });

  // Subscribe to language changes to re-render tabs
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['type', 'issueDate', 'customerId'];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Initialize items when data changes
  useEffect(() => {
    setItems(data?.items || []);
  }, [data]);

  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Build tabs
  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      viewMode,
      data,
      items,
      setItems,
    };
    const tabsLocal = buildTabsArray(childProps);
    buildTabs(tabsLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initTab, viewMode, data, items, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    async ({ values, allValues }) => {
      const documentId = allValues?._id || data?._id;

      // Calculate totals from items
      const totals = calculateTotals(items);

      // Build data to save
      const dataToSave = {
        ...values,
        items: items.map((item, index) => ({
          ...item,
          sortOrder: index + 1,
        })),
        ...totals,
      };

      await upsert({ dataSet: dataToSave, _id: documentId });
      return true;
    },
    [data, items, upsert]
  );

  const handleAddItem = useCallback(newItem => {
    setItems(prev => [...prev, { ...newItem, sortOrder: prev.length + 1 }]);
  }, []);

  const handleUpdateItem = useCallback((index, updatedItem) => {
    setItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, ...updatedItem } : item))
    );
  }, []);

  const handleRemoveItem = useCallback(index => {
    setItems(prev =>
      prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({
          ...item,
          sortOrder: i + 1,
        }))
    );
  }, []);

  // Status transition handlers
  const handleStatusChange = useCallback(
    async newStatus => {
      const documentId = data?._id;
      if (!documentId) return;

      try {
        await upsert({ dataSet: { status: newStatus }, _id: documentId });
        refresh?.();
      } catch (err) {
        throw err;
      }
    },
    [data?._id, upsert, refresh]
  );

  const handleConvertToOrder = useCallback(async () => {
    const documentId = data?._id;
    if (!documentId) return;

    try {
      await upsert({
        dataSet: { type: 'order', status: 'open', convertedFromBudget: documentId },
        _id: documentId,
      });
      refresh?.();
    } catch (err) {
      throw err;
    }
  }, [data?._id, upsert, refresh]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(
    () => ({
      ...(data || {}),
      type: data?.type || 'budget',
      status: data?.status || 'draft',
      issueDate: data?.issueDate || new Date(),
      priority: data?.priority || 'normal',
    }),
    [data]
  );

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // Build float actions based on status
  const floatActions = serviceOrdersDocumentFloatButton({
    data,
    handlers: {
      onSendToCustomer: () => handleStatusChange('sent'),
      onMarkApproved: () => handleStatusChange('approved'),
      onMarkRejected: () => handleStatusChange('rejected'),
      onConvertToOrder: handleConvertToOrder,
      onStartWork: () => handleStatusChange('in_progress'),
      onMarkCompleted: () => handleStatusChange('completed'),
      onWaitingParts: () => handleStatusChange('waiting_parts'),
      onResumeWork: () => handleStatusChange('in_progress'),
      onMarkDelivered: () => handleStatusChange('delivered'),
    },
  });

  // Check if status is locked (no editing allowed)
  const isLocked = viewMode === 'view' || isLockedStatus(data?.status);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          requiredFields={requiredFields}
          locked={isLocked}
          showInfoMessage={true}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={STYLES.cardHeader}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.customerSnapshot?.name || data?.customerName}
              watchField="type"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>

      {/* Float action button for status transitions */}
      {floatActions.length > 0 && <ActionsFloatButton floatActions={floatActions} />}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServiceOrdersDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build tabs array for the document
 */
function buildTabsArray(props) {
  const { initTab, viewMode, items, setItems } = props;
  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      label: (
        <span style={TAB_LABEL_STYLE}>
          <FormOutlined />
          {translateX('record_tab')}
        </span>
      ),
      hide: false,
      disabled: false,
      children: <ServiceOrdersRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'items',
      label: (
        <span style={TAB_LABEL_STYLE}>
          <UnorderedListOutlined />
          {translateX('items_tab')}
        </span>
      ),
      hide: false,
      disabled: isNewDocument,
      children: (
        <ServiceOrdersItemsTab
          {...props}
          tabName="items"
          items={items}
          setItems={setItems}
        />
      ),
    },
  ];
}

/**
 * Calculate totals from items array
 */
function calculateTotals(items) {
  let servicesTotal = 0;
  let productsTotal = 0;
  let discountTotal = 0;

  (items || []).forEach(item => {
    const lineTotal = (item.quantity || 1) * (item.unitPrice || 0);
    const lineDiscount = item.discountValue || 0;

    if (item.itemType === 'service') {
      servicesTotal += lineTotal - lineDiscount;
    } else {
      productsTotal += lineTotal - lineDiscount;
    }
    discountTotal += lineDiscount;
  });

  const grandTotal = servicesTotal + productsTotal;

  return {
    servicesTotal: Math.round(servicesTotal * 100) / 100,
    productsTotal: Math.round(productsTotal * 100) / 100,
    discountTotal: Math.round(discountTotal * 100) / 100,
    grandTotal: Math.round(grandTotal * 100) / 100,
  };
}

/**
 * Check if a status is locked (no editing allowed)
 */
function isLockedStatus(status) {
  const lockedStatuses = ['converted', 'invoiced', 'delivered', 'canceled'];
  return lockedStatuses.includes(status);
}
